/**
 * User Management Component
 * CRUD operations for users and company assignments
 */
import { useState, useEffect } from 'react';
import api from '../utils/api';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [parentCompanies, setParentCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showParentCompanyModal, setShowParentCompanyModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedParentCompanies, setSelectedParentCompanies] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    user_type: 'user',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, parentCompaniesRes] = await Promise.all([
        api.get('/users'),
        api.get('/parent-companies'),
      ]);
      setUsers(usersRes.data);
      setParentCompanies(parentCompaniesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', formData);
      setShowCreateForm(false);
      setFormData({ email: '', password: '', full_name: '', user_type: 'user' });
      loadData();
      alert('User created successfully');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '', // Leave empty for security
      full_name: user.full_name || '',
      user_type: user.user_type,
    });
    setShowEditForm(true);
    setShowCreateForm(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      // Only send fields that have values
      const updateData = {
        email: formData.email,
        full_name: formData.full_name,
        user_type: formData.user_type,
      };
      // Only include password if it's been changed
      if (formData.password) {
        updateData.password = formData.password;
      }

      await api.put(`/users/${selectedUser.user_id}`, updateData);
      setShowEditForm(false);
      setSelectedUser(null);
      setFormData({ email: '', password: '', full_name: '', user_type: 'user' });
      loadData();
      alert('User updated successfully');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setSelectedUser(null);
    setFormData({ email: '', password: '', full_name: '', user_type: 'user' });
  };

  const handleDeactivate = async (userId) => {
    if (!confirm('Are you sure you want to deactivate this user?')) return;

    try {
      await api.delete(`/users/${userId}`);
      loadData();
      alert('User deactivated successfully');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const handleOpenParentCompanyModal = (user) => {
    setSelectedUser(user);
    // Pre-select currently assigned parent companies
    const currentParentCompanyIds = user.parent_companies.map((pc) => pc.parent_company_id);
    setSelectedParentCompanies(currentParentCompanyIds);
    setShowParentCompanyModal(true);
    setShowCreateForm(false);
    setShowEditForm(false);
  };

  const handleToggleParentCompany = (parentCompanyId) => {
    setSelectedParentCompanies((prev) =>
      prev.includes(parentCompanyId)
        ? prev.filter((id) => id !== parentCompanyId)
        : [...prev, parentCompanyId]
    );
  };

  const handleSaveParentCompanies = async () => {
    if (!selectedUser) return;

    // Get currently assigned parent company IDs
    const currentParentCompanyIds = selectedUser.parent_companies.map((pc) => pc.parent_company_id);

    // Find parent companies to add (selected but not currently assigned)
    const parentCompaniesToAdd = selectedParentCompanies.filter(
      (id) => !currentParentCompanyIds.includes(id)
    );

    // Find parent companies to remove (currently assigned but not selected)
    const parentCompaniesToRemove = currentParentCompanyIds.filter(
      (id) => !selectedParentCompanies.includes(id)
    );

    try {
      // Add new parent companies
      if (parentCompaniesToAdd.length > 0) {
        await api.post(`/users/${selectedUser.user_id}/parent-companies`, {
          parent_company_ids: parentCompaniesToAdd,
        });
      }

      // Remove unselected parent companies
      for (const parentCompanyId of parentCompaniesToRemove) {
        await api.delete(`/users/${selectedUser.user_id}/parent-companies/${parentCompanyId}`);
      }

      setShowParentCompanyModal(false);
      setSelectedUser(null);
      setSelectedParentCompanies([]);
      loadData();
      alert('Parent companies updated successfully');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update parent companies');
    }
  };

  const handleCloseParentCompanyModal = () => {
    setShowParentCompanyModal(false);
    setSelectedUser(null);
    setSelectedParentCompanies([]);
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="user-management">
      <div className="management-header">
        <h2>Users</h2>
        <button onClick={() => setShowCreateForm(!showCreateForm)} className="btn-primary">
          {showCreateForm ? 'Cancel' : '+ Create User'}
        </button>
      </div>

      {showCreateForm && (
        <form onSubmit={handleCreate} className="create-form">
          <h3>Create New User</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={8}
              />
            </div>
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>User Type</label>
              <select
                value={formData.user_type}
                onChange={(e) => setFormData({ ...formData, user_type: e.target.value })}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <button type="submit" className="btn-primary">Create User</button>
        </form>
      )}

      {showEditForm && (
        <form onSubmit={handleUpdate} className="create-form">
          <h3>Edit User: {selectedUser?.email}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Password (leave empty to keep current)</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                minLength={8}
                placeholder="Leave empty to keep current password"
              />
            </div>
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>User Type</label>
              <select
                value={formData.user_type}
                onChange={(e) => setFormData({ ...formData, user_type: e.target.value })}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" className="btn-primary">Update User</button>
            <button type="button" onClick={handleCancelEdit} className="btn-secondary">Cancel</button>
          </div>
        </form>
      )}

      {showParentCompanyModal && (
        <div className="modal-overlay" onClick={handleCloseParentCompanyModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Manage Parent Companies for {selectedUser?.email}</h3>
            <p style={{ marginBottom: '1rem', color: '#666' }}>
              Select parent companies to assign to this user. Users will have access to all companies under the selected parent companies.
            </p>
            <div className="company-list">
              {parentCompanies.map((parentCompany) => (
                <label key={parentCompany.parent_company_id} className="company-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedParentCompanies.includes(parentCompany.parent_company_id)}
                    onChange={() => handleToggleParentCompany(parentCompany.parent_company_id)}
                  />
                  <span className="company-label">
                    {parentCompany.parent_company_name}
                    {parentCompany.description && (
                      <span className="company-parent"> - {parentCompany.description}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
            <div className="modal-actions">
              <button onClick={handleSaveParentCompanies} className="btn-primary">
                Save Changes
              </button>
              <button onClick={handleCloseParentCompanyModal} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Email</th>
            <th>Full Name</th>
            <th>Type</th>
            <th>Status</th>
            <th>Parent Companies</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.user_id}>
              <td>{user.user_id}</td>
              <td>{user.email}</td>
              <td>{user.full_name || '-'}</td>
              <td>
                <span className={`badge badge-${user.user_type}`}>{user.user_type}</span>
              </td>
              <td>
                <span className={`badge badge-${user.is_active ? 'active' : 'inactive'}`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td>
                {user.parent_companies && user.parent_companies.length > 0
                  ? user.parent_companies
                      .map((pc) => pc.parent_company_name)
                      .join(', ')
                  : 'None'}
              </td>
              <td>
                <div className="action-buttons">
                  <div className="action-group-primary">
                    <button
                      onClick={() => handleEdit(user)}
                      className="btn-small"
                      title="Edit user details"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleOpenParentCompanyModal(user)}
                      className="btn-small"
                      title="Manage parent company assignments"
                    >
                      Manage Parent Companies
                    </button>
                  </div>
                  {user.is_active && (
                    <div className="action-group-danger">
                      <button
                        onClick={() => handleDeactivate(user.user_id)}
                        className="btn-small btn-danger"
                        title="Deactivate user account"
                      >
                        Deactivate
                      </button>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default UserManagement;
